import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout'; // Includes Header and Footer
import StepNavigation from '../components/StepNavigation';
import PersonalInfoStep from '../components/steps/PersonalInfoStep';
import LicenseUploadStep from '../components/steps/LicenseUploadStep';
import BillingPaymentStep from '../components/steps/BillingPaymentStep';
import axios from 'axios';
import BillingPaymentStepWrapper from '../components/steps/BillingPaymentStep';

const steps = [
    { label: 'Personal Info', component: PersonalInfoStep },
    { label: 'License Upload', component: LicenseUploadStep },
    { label: 'Billing & Payment', component: BillingPaymentStepWrapper },
];

function Checkout() {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({}); // To store data across steps
    const [carDetails, setCarDetails] = useState(null); // Store car details
    const location = useLocation();
    const { carId, totalPrice, startDate, endDate } = location.state || {};

    console.log('Current Step:', currentStep);

    // Calculate rental days and price per day
    const calculateRentalDays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24))); // At least 1 day
    };

    const rentalDays = startDate && endDate ? calculateRentalDays(startDate, endDate) : 0;
    const pricePerDay = totalPrice && rentalDays ? totalPrice / rentalDays : 0;

    useEffect(() => {
        document.title = "Book Your Rental | KAR – Kelowna Auto Rentals";
        document.querySelector('meta[name="description"]')?.setAttribute("content",
            "Complete your car rental booking with KAR – Kelowna Auto Rentals. Secure checkout, no hidden fees. Contactless pickup available at YLW & YVR."
        );
    }, []);

    // Initialize formData only once
    useEffect(() => {
        if (!formData.startDate && startDate) {
            setFormData((prev) => ({
                ...prev,
                startDate,
                endDate,
                totalPrice,
                rentalDays,
                pricePerDay,
            }));
        }
    }, [startDate, endDate, totalPrice, rentalDays, pricePerDay]);

    // Fetch car details using carId
    useEffect(() => {
        const fetchCarDetails = async () => {
            if (!carId) return;

            try {
                const response = await axios.get('https://api.karrentals.ca/search-cars', {
                    params: { car_id: carId },
                });
                if (response.data.success && response.data.data.length > 0) {
                    setCarDetails(response.data.data[0]);
                } else {
                    console.error('Car details not found.');
                }
            } catch (err) {
                console.error('Error fetching car details:', err);
            }
        };

        fetchCarDetails();
    }, [carId]);

    const handleNext = (stepData) => {
        setFormData((prev) => ({ ...prev, ...stepData }));
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1)); // Ensure it doesn't exceed steps length
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0)); // Ensure it doesn't go below 0
    };

    const CurrentStepComponent = steps[currentStep]?.component;

    if (!CurrentStepComponent) {
        return (
            <Layout>
                <div className="container mx-auto mt-4 rounded-2xl bg-white">
                    <div className="text-center py-8">
                        <h2 className="text-2xl font-bold">Invalid Step</h2>
                        <p>Please refresh the page or contact support if this issue persists.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container mx-auto mt-4 p-4">
                <div className="rounded-2xl bg-white">
                    {/* Step Navigation */}
                    <div className="py-6">
                        <StepNavigation
                            steps={steps}
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="container mx-auto flex-grow flex items-center justify-center py-8">
                        <div className="max-w-3xl w-full bg-white text-black p-6 rounded-lg shadow-lg">
                            <div className="mb-4 space-y-2">
                                <p>
                                    {carDetails
                                        ? `${carDetails.year} ${carDetails.name} ${carDetails.model}`
                                        : 'Loading...'}
                                </p>
                                <p className="flex items-center justify-normal">
                                    <span>
                                        <strong>Total Price:</strong> ${totalPrice}
                                    </span>
                                    <span className="italic text-xs mx-2 text-gray-600">
                                        excluding Taxes and Fees
                                    </span>
                                </p>
                                <p className="flex items-center justify-normal">
                                    <span>
                                        {startDate
                                            ? new Date(startDate).toLocaleDateString()
                                            : 'N/A'}
                                    </span>
                                    <span> - </span>
                                    <span>
                                        {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                                    </span>
                                </p>
                            </div>
                            {React.createElement(CurrentStepComponent, {
                                onNext: handleNext,
                                onBack: handleBack,
                                formData: {
                                    ...formData,
                                    totalPrice,
                                    rentalDays,
                                    pricePerDay,
                                    startDate,
                                    endDate,
                                    carDetails,
                                    carId,
                                },
                            })}
                        </div>
                    </div>
                    <img src="/checkoutlandscape.png" alt="Ad Banner" />
                </div>
            </div>
        </Layout>
    );
}

export default Checkout;